import type { Capture, Move, Side, VictoryConfig } from "../engine";
import type { ClientMessage, ServerMessage } from "../../worker/src/protocol";
import { loadToken } from "../auth/storage";

/** Returns the API base URL. In production, set VITE_WORKER_URL. In dev, defaults to same-origin (proxied by Vite). */
function apiBase(): string {
  const url = (import.meta.env.VITE_WORKER_URL as string | undefined) ?? "";
  return url.replace(/\/+$/, "");
}

/** Returns the WebSocket base URL (ws:// or wss://) corresponding to the API base. */
function wsBase(): string {
  const base = apiBase();
  if (base) {
    return base.replace(/^http/, "ws");
  }
  // Same-origin: derive from window.location
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${window.location.host}`;
}

export async function createGame(victoryConfig?: VictoryConfig): Promise<{ code: string }> {
  const res = await fetch(`${apiBase()}/api/game`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ victoryConfig }),
  });
  if (!res.ok) throw new Error(`failed to create game: ${res.status}`);
  return (await res.json()) as { code: string };
}

export interface NetClient {
  send(msg: ClientMessage): void;
  close(): void;
  on(event: "message", handler: (msg: ServerMessage) => void): void;
  on(event: "open" | "close", handler: () => void): void;
  on(event: "error", handler: (err: Event) => void): void;
}

interface InternalHandlers {
  message: Array<(msg: ServerMessage) => void>;
  open: Array<() => void>;
  close: Array<() => void>;
  error: Array<(err: Event) => void>;
}

export function connect(code: string, opts?: { rejoinToken?: string; preferredSide?: Side }): NetClient {
  const params = new URLSearchParams();
  if (opts?.rejoinToken) params.set("token", opts.rejoinToken);
  if (opts?.preferredSide) params.set("side", opts.preferredSide);
  // Browser WebSocket constructors can't set custom headers, so the session token rides along
  // as a query param. The server resolves it to a user id (or ignores it for anonymous play).
  const authToken = loadToken();
  if (authToken) params.set("auth", authToken);
  const qs = params.toString();
  const url = `${wsBase()}/api/game/${code.toUpperCase()}/ws${qs ? `?${qs}` : ""}`;
  const ws = new WebSocket(url);
  const handlers: InternalHandlers = { message: [], open: [], close: [], error: [] };

  ws.addEventListener("open", () => handlers.open.forEach((h) => h()));
  ws.addEventListener("close", () => handlers.close.forEach((h) => h()));
  ws.addEventListener("error", (e) => handlers.error.forEach((h) => h(e)));
  ws.addEventListener("message", (event) => {
    let msg: ServerMessage;
    try {
      msg = JSON.parse(event.data as string);
    } catch {
      return;
    }
    handlers.message.forEach((h) => h(msg));
  });

  return {
    send(msg: ClientMessage) {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(msg));
      }
    },
    close() {
      ws.close();
    },
    on(event, handler) {
      if (event === "message") handlers.message.push(handler as (msg: ServerMessage) => void);
      else if (event === "open") handlers.open.push(handler as () => void);
      else if (event === "close") handlers.close.push(handler as () => void);
      else if (event === "error") handlers.error.push(handler as (err: Event) => void);
    },
  };
}

export function sendMove(client: NetClient, move: Move, declared?: Capture, advance?: boolean): void {
  client.send({ type: "move", move, declared, advance });
}

export function sendResign(client: NetClient): void {
  client.send({ type: "resign" });
}
